import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, Plus, Loader2, Trash2, FileDown, Check, Users, Calendar, Clock, Mail,
  Camera, StickyNote, Mic, AlertTriangle,
} from 'lucide-react';
import { SendDocumentEmailDialog } from '../components/documents/SendDocumentEmailDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABEL = { present: 'Présent', absent: 'Absent', excused: 'Excusé' };
const STATUS_COLOR = {
  present: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  absent: 'bg-red-100 text-red-800 border-red-300',
  excused: 'bg-amber-100 text-amber-800 border-amber-300',
};

export function MeetingReportsPage() {
  const { chantierId } = useParams();
  const navigate = useNavigate();
  const [chantier, setChantier] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cRes, rRes] = await Promise.all([
        axios.get(`${API}/chantiers/${chantierId}`, { withCredentials: true }),
        axios.get(`${API}/meeting-reports?chantier_id=${chantierId}`, { withCredentials: true }),
      ]);
      setChantier(cRes.data);
      setReports(rRes.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  const createReport = async () => {
    setCreating(true);
    try {
      // Preremplir les entreprises depuis les lots du chantier
      const attendees = (chantier?.lots || chantier?.entreprises || []).map(x => ({
        entreprise_id: x.entreprise_id || x.id,
        entreprise_name: x.entreprise_name || x.name || '',
        lot_name: x.lot_name || x.name || '',
        contact_name: x.contact_name || '',
        status: 'present',
        note: '',
      }));
      const r = await axios.post(`${API}/meeting-reports`, {
        chantier_id: chantierId,
        meeting_date: new Date().toISOString().slice(0, 10),
        meeting_time: '14:00',
        attendees,
        agenda_items: [],
        next_meeting: null,
      }, { withCredentials: true });
      toast.success(`CR n°${r.data.meeting_number} créé`);
      setEditingReport(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce compte rendu ?')) return;
    try {
      await axios.delete(`${API}/meeting-reports/${id}`, { withCredentials: true });
      toast.success('Supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  const downloadPDF = async (id, num) => {
    try {
      const r = await axios.get(`${API}/meeting-reports/${id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `CR_reunion_${num}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement PDF'); }
  };

  const [emailingReport, setEmailingReport] = useState(null);

  if (loading) return <AppShell><div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div></AppShell>;

  if (editingReport) {
    return <ReportEditor report={editingReport} onSaved={() => { setEditingReport(null); load(); }} onCancel={() => setEditingReport(null)} />;
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <button onClick={() => navigate(`/chantiers/${chantierId}`)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-testid="back-btn">
          <ArrowLeft size={16} /> Retour chantier
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">Comptes rendus de réunion</h1>
            <p className="text-sm text-gray-500">{chantier?.name}</p>
          </div>
          <Button onClick={createReport} disabled={creating} className="bg-[#0055FF] hover:bg-blue-700" data-testid="create-cr-btn">
            {creating ? <Loader2 className="animate-spin h-4 w-4" /> : <><Plus size={16} className="mr-1" /> Nouveau CR</>}
          </Button>
        </div>

        {reports.length === 0 ? (
          <Card className="border-dashed"><CardContent className="p-8 text-center text-gray-500">
            Aucun compte rendu pour ce chantier. Cliquez « Nouveau CR » pour créer votre premier compte rendu de réunion.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {reports.map(r => (
              <Card key={r.id} className="hover:shadow-md transition" data-testid={`cr-card-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[#111827]">CR n°{r.meeting_number}</span>
                        <span className="text-sm text-gray-600">— {r.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'finalized' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.status === 'finalized' ? 'Finalisé' : 'Brouillon'}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                        <span><Calendar size={12} className="inline mr-0.5" />{new Date(r.meeting_date).toLocaleDateString('fr-FR')}</span>
                        {r.meeting_time && <span><Clock size={12} className="inline mr-0.5" />{r.meeting_time}</span>}
                        <span><Users size={12} className="inline mr-0.5" />{(r.attendees || []).length}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setEditingReport(r)} data-testid={`edit-cr-${r.id}`}>Éditer</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(r.id, r.meeting_number)} data-testid={`pdf-cr-${r.id}`}>
                        <FileDown size={14} className="mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEmailingReport(r)}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              data-testid={`email-cr-${r.id}`}>
                        <Mail size={14} className="mr-1" /> Envoyer
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => del(r.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`del-cr-${r.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <SendDocumentEmailDialog
        open={!!emailingReport}
        onClose={() => setEmailingReport(null)}
        docType="meeting_report"
        docId={emailingReport?.id}
        docTitle={emailingReport ? `CR n°${emailingReport.meeting_number} — ${emailingReport.title || ''}` : ''}
        suggestedRecipients={
          emailingReport
            ? (emailingReport.attendees || []).map(a => ({
                email: a.contact_email || a.email || '',
                name: a.contact_name || '',
                label: `${a.entreprise_name}${a.lot_name ? ' — ' + a.lot_name : ''}${a.contact_name ? ' (' + a.contact_name + ')' : ''}`,
              }))
            : []
        }
      />
    </AppShell>
  );
}

function ReportEditor({ report, onSaved, onCancel }) {
  const [doc, setDoc] = useState(report);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [fieldItems, setFieldItems] = useState([]);

  // Charger les elements terrain agreges (photos, notes, reserves, vocaux)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/meeting-reports/${report.id}`, { withCredentials: true });
        setFieldItems(r.data.field_items || []);
      } catch { /* ignore */ }
    })();
  }, [report.id]);

  const updateField = (k, v) => setDoc(d => ({ ...d, [k]: v }));
  const updateAttendee = (i, patch) => setDoc(d => ({ ...d, attendees: d.attendees.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
  const removeAttendee = (i) => setDoc(d => ({ ...d, attendees: d.attendees.filter((_, idx) => idx !== i) }));
  const addAttendee = () => setDoc(d => ({ ...d, attendees: [...(d.attendees || []), { entreprise_name: '', lot_name: '', contact_name: '', status: 'present', note: '' }] }));

  const updateAgenda = (i, patch) => setDoc(d => ({ ...d, agenda_items: d.agenda_items.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
  const removeAgenda = (i) => setDoc(d => ({ ...d, agenda_items: d.agenda_items.filter((_, idx) => idx !== i) }));
  const addAgenda = () => setDoc(d => ({ ...d, agenda_items: [...(d.agenda_items || []), { topic: '', details: '', decision: '', responsable: '', deadline: '' }] }));

  const save = async (finalize = false) => {
    if (finalize) setFinalizing(true); else setSaving(true);
    try {
      const payload = {
        meeting_date: doc.meeting_date,
        meeting_time: doc.meeting_time,
        title: doc.title,
        attendees: doc.attendees || [],
        agenda_items: doc.agenda_items || [],
        next_meeting: doc.next_meeting,
        context: doc.context || '',
      };
      await axios.put(`${API}/meeting-reports/${doc.id}`, payload, { withCredentials: true });
      if (finalize) {
        await axios.post(`${API}/meeting-reports/${doc.id}/finalize`, {}, { withCredentials: true });
        toast.success('Compte rendu finalisé');
      } else {
        toast.success('Enregistré');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); setFinalizing(false); }
  };

  const nm = doc.next_meeting || { date: '', time: '', location: '', note: '' };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <button onClick={onCancel} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-testid="editor-back-btn">
          <ArrowLeft size={16} /> Retour à la liste
        </button>
        <Card><CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Date de la réunion</Label>
              <Input type="date" value={doc.meeting_date || ''} onChange={e => updateField('meeting_date', e.target.value)} data-testid="cr-date" /></div>
            <div><Label>Heure</Label>
              <Input type="time" value={doc.meeting_time || ''} onChange={e => updateField('meeting_time', e.target.value)} data-testid="cr-time" /></div>
            <div><Label>Titre</Label>
              <Input value={doc.title || ''} onChange={e => updateField('title', e.target.value)} placeholder={`CR n°${doc.meeting_number}`} data-testid="cr-title" /></div>
          </div>
          <div><Label>Contexte / Introduction</Label>
            <textarea rows={2} className="w-full border rounded-md p-2 text-sm"
              value={doc.context || ''} onChange={e => updateField('context', e.target.value)} data-testid="cr-context" /></div>
        </CardContent></Card>

        {/* Participants */}
        <Card><CardContent className="p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-[#111827] flex items-center gap-2"><Users size={18} />Participants ({doc.attendees?.length || 0})</h3>
            <Button size="sm" variant="outline" onClick={addAttendee} data-testid="add-attendee-btn"><Plus size={14} className="mr-1" />Ajouter</Button>
          </div>
          <div className="space-y-2">
            {(doc.attendees || []).map((a, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 border rounded-lg bg-gray-50">
                <Input className="md:col-span-3" value={a.entreprise_name} onChange={e => updateAttendee(i, { entreprise_name: e.target.value })} placeholder="Entreprise" data-testid={`att-ent-${i}`} />
                <Input className="md:col-span-2" value={a.lot_name || ''} onChange={e => updateAttendee(i, { lot_name: e.target.value })} placeholder="Lot" />
                <Input className="md:col-span-3" value={a.contact_name || ''} onChange={e => updateAttendee(i, { contact_name: e.target.value })} placeholder="Contact" />
                <div className="md:col-span-3 flex gap-1">
                  {['present', 'absent', 'excused'].map(s => (
                    <button key={s} onClick={() => updateAttendee(i, { status: s })}
                      className={`flex-1 px-2 py-1.5 text-xs rounded border-2 font-semibold ${a.status === s ? STATUS_COLOR[s] : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-100'}`}
                      data-testid={`att-${i}-status-${s}`}>
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
                <button onClick={() => removeAttendee(i)} className="md:col-span-1 text-red-500 hover:bg-red-50 rounded p-1 flex items-center justify-center" data-testid={`att-remove-${i}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {(!doc.attendees || doc.attendees.length === 0) && <p className="text-xs text-gray-400 text-center py-4">Aucun participant. Cliquez « Ajouter ».</p>}
          </div>
        </CardContent></Card>

        {/* Points abordés */}
        <Card><CardContent className="p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-[#111827]">Points abordés ({doc.agenda_items?.length || 0})</h3>
            <Button size="sm" variant="outline" onClick={addAgenda} data-testid="add-agenda-btn"><Plus size={14} className="mr-1" />Ajouter</Button>
          </div>
          <div className="space-y-2">
            {(doc.agenda_items || []).map((it, i) => (
              <div key={i} className="p-3 border rounded-lg bg-gray-50 space-y-2">
                <div className="flex gap-2">
                  <span className="font-bold text-sm text-gray-500 flex-shrink-0 mt-2">{i + 1}.</span>
                  <Input value={it.topic || ''} onChange={e => updateAgenda(i, { topic: e.target.value })} placeholder="Sujet" data-testid={`agenda-topic-${i}`} className="flex-1" />
                  <button onClick={() => removeAgenda(i)} className="text-red-500 p-2"><Trash2 size={16} /></button>
                </div>
                <textarea rows={2} value={it.details || ''} onChange={e => updateAgenda(i, { details: e.target.value })}
                  placeholder="Détails / discussions" className="w-full border rounded p-2 text-sm" />
                <Input value={it.decision || ''} onChange={e => updateAgenda(i, { decision: e.target.value })} placeholder="Décision prise" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={it.responsable || ''} onChange={e => updateAgenda(i, { responsable: e.target.value })} placeholder="Responsable" />
                  <Input value={it.deadline || ''} onChange={e => updateAgenda(i, { deadline: e.target.value })} placeholder="Échéance" />
                </div>
              </div>
            ))}
            {(!doc.agenda_items || doc.agenda_items.length === 0) && <p className="text-xs text-gray-400 text-center py-4">Aucun point. Cliquez « Ajouter ».</p>}
          </div>
        </CardContent></Card>

        {/* Convocation prochaine réunion */}
        <Card><CardContent className="p-5">
          <h3 className="font-bold text-[#111827] mb-3 flex items-center gap-2"><Calendar size={18} />Convocation à la prochaine réunion</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Date</Label>
              <Input type="date" value={nm.date || ''} onChange={e => updateField('next_meeting', { ...nm, date: e.target.value })} data-testid="cr-next-date" /></div>
            <div><Label>Heure</Label>
              <Input type="time" value={nm.time || ''} onChange={e => updateField('next_meeting', { ...nm, time: e.target.value })} data-testid="cr-next-time" /></div>
            <div><Label>Lieu</Label>
              <Input value={nm.location || ''} onChange={e => updateField('next_meeting', { ...nm, location: e.target.value })} placeholder="Bureau / Chantier" /></div>
          </div>
          <div className="mt-2"><Label>Note</Label>
            <Input value={nm.note || ''} onChange={e => updateField('next_meeting', { ...nm, note: e.target.value })} placeholder="Ordre du jour prévisionnel..." /></div>
        </CardContent></Card>

        {/* Éléments terrain agrégés du chantier */}
        <FieldItemsPanel items={fieldItems} />

        <div className="flex gap-2 justify-end sticky bottom-4 bg-white/95 backdrop-blur p-3 rounded-lg border shadow-lg">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={() => save(false)} disabled={saving || finalizing || doc.status === 'finalized'} data-testid="save-cr-btn">
            {saving ? <Loader2 className="animate-spin h-4 w-4" /> : 'Enregistrer'}
          </Button>
          <Button onClick={() => save(true)} disabled={saving || finalizing || doc.status === 'finalized'} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finalize-cr-btn">
            {finalizing ? <Loader2 className="animate-spin h-4 w-4" /> : <><Check size={16} className="mr-1" />Finaliser & générer PDF</>}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

const PRIO_STYLE = {
  critical: 'text-red-800 bg-red-100 border-red-300',
  high: 'text-red-700 bg-red-50 border-red-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low: 'text-emerald-700 bg-emerald-50 border-emerald-200',
};

function FieldItemsPanel({ items }) {
  const reserves = items.filter(i => i.type === 'reserve');
  const photos = items.filter(i => i.type === 'photo');
  const notes = items.filter(i => i.type === 'text');
  const voices = items.filter(i => i.type === 'voice');
  const total = items.length;

  if (total === 0) {
    return (
      <Card className="border-dashed"><CardContent className="p-5 text-center text-sm text-gray-500">
        <AlertTriangle size={20} className="mx-auto mb-2 text-amber-500" />
        Aucun élément terrain (photo, note, réserve) n&apos;a encore été enregistré sur ce chantier.
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#111827] flex items-center gap-2">
            📎 Éléments terrain du chantier ({total})
          </h3>
          <span className="text-xs text-gray-500">
            {photos.length > 0 && `${photos.length} photo${photos.length > 1 ? 's' : ''} · `}
            {reserves.length > 0 && `${reserves.length} réserve${reserves.length > 1 ? 's' : ''} · `}
            {notes.length > 0 && `${notes.length} note${notes.length > 1 ? 's' : ''} · `}
            {voices.length > 0 && `${voices.length} vocale${voices.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <p className="text-xs text-gray-500 -mt-3">
          Ces éléments seront listés automatiquement dans le PDF du compte rendu.
        </p>

        {reserves.length > 0 && (
          <section>
            <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
              <AlertTriangle size={14} /> Réserves ({reserves.length})
            </h4>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {reserves.map(r => (
                <div key={r.id} className={`text-xs p-2 border rounded ${PRIO_STYLE[r.priority] || 'bg-gray-50'}`}>
                  <div className="font-semibold">{r.text}</div>
                  {r.description && <div className="opacity-80 mt-0.5 line-clamp-2">{r.description}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {photos.length > 0 && (
          <section>
            <h4 className="text-sm font-semibold text-[#0055FF] mb-2 flex items-center gap-1.5">
              <Camera size={14} /> Photos ({photos.length})
            </h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-60 overflow-y-auto">
              {photos.map(p => (
                <div key={p.id} className="border rounded overflow-hidden bg-gray-50">
                  <img src={`${API}/files/${p.id}/download`} alt={p.caption}
                    className="w-full h-16 object-cover" loading="lazy" />
                  {p.caption && <div className="text-[10px] text-gray-500 truncate px-1">{p.caption}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {notes.length > 0 && (
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <StickyNote size={14} /> Notes ({notes.length})
            </h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {notes.map(n => (
                <div key={n.id} className="text-xs p-2 bg-gray-50 border rounded">
                  <div className="text-gray-800 line-clamp-2">{n.text}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {voices.length > 0 && (
          <section>
            <h4 className="text-sm font-semibold text-purple-700 mb-2 flex items-center gap-1.5">
              <Mic size={14} /> Notes vocales ({voices.length})
            </h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {voices.map(v => (
                <div key={v.id} className="text-xs p-2 bg-purple-50 border border-purple-100 rounded">
                  <div className="text-gray-800 line-clamp-2">{v.text}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}

